import { ActionIcon, Group, Tooltip } from '@mantine/core'
import { IconMailForward, IconPencil, IconX } from '@tabler/icons-react'
import { ReactNode } from 'react'
import { FormattedMessage } from 'react-intl'
import { WorkspaceMemberRole, WorkspaceMemberStatus } from '@/types/workspace'

type ActionButtonProps = {
  tooltip: ReactNode
  icon: ReactNode
  onClick: () => void
}

const ActionButton = ({ tooltip, icon, onClick }: ActionButtonProps) => {
  return (
    <Tooltip label={tooltip}>
      <ActionIcon variant="subtle" size="md" radius="xl" onClick={onClick}>
        {icon}
      </ActionIcon>
    </Tooltip>
  )
}

type MembersListActionsProps = {
  isCurrentUser: boolean
  memberRole: WorkspaceMemberRole
  memberStatus: WorkspaceMemberStatus
  canResendInvite: boolean
  canUpdateMember: boolean
  canDeleteMember: boolean
  onResendInvite: () => void
  onUpdate: () => void
  onDeactivate: () => void
}

export const MembersListActions = ({
  isCurrentUser,
  memberRole,
  memberStatus,
  canResendInvite,
  canUpdateMember,
  canDeleteMember,
  onResendInvite,
  onUpdate,
  onDeactivate
}: MembersListActionsProps) => {
  if (isCurrentUser || memberRole === WorkspaceMemberRole.Owner) {
    return null
  }

  return (
    <Group wrap="nowrap" gap={4} justify="end">
      {canResendInvite && memberStatus !== WorkspaceMemberStatus.Active && (
        <ActionButton
          tooltip={
            <FormattedMessage id="workspace.members.list.actions.resend" />
          }
          icon={
            <IconMailForward
              style={{ width: '80%', height: '80%' }}
              color="var(--mantine-color-yellow-6)"
            />
          }
          onClick={onResendInvite}
        />
      )}

      {canUpdateMember && memberStatus === WorkspaceMemberStatus.Active && (
        <ActionButton
          tooltip={
            <FormattedMessage id="workspace.members.list.actions.update" />
          }
          icon={
            <IconPencil
              style={{ width: '80%', height: '80%' }}
              color="var(--mantine-color-green-6)"
            />
          }
          onClick={onUpdate}
        />
      )}

      {canDeleteMember && memberStatus === WorkspaceMemberStatus.Active && (
        <ActionButton
          tooltip={
            <FormattedMessage id="workspace.members.list.actions.deactivate" />
          }
          icon={
            <IconX
              style={{ width: '80%', height: '80%' }}
              color="var(--mantine-color-red-6)"
            />
          }
          onClick={onDeactivate}
        />
      )}
    </Group>
  )
}
